import SubjectTabs from '../generic/SubjectTabs';
import { CaretRightIcon } from '@phosphor-icons/react';
import { useState, useEffect } from 'react';
import StatCell from '../generic/StatCell';
import { useNavigate } from 'react-router-dom';
import { secondsToMinutes } from '../../../../../apps/frontend/src/utils/utilFunctions';
import { findPercentile } from '../../../../../apps/frontend/src/utils/percentile';
import TableHeader from '../../components/generic/TableHeader';
import { QSTATUS } from '../../../../../apps/frontend/src/utils/strings';
import NoData from '../generic/NoData';

const TimelineBreakdown = ({ data, loading, packId, pack, onResetFilters }) => {
  const router = useNavigate();
  const [activeSubject, setActiveSubject] = useState('overall');
  const [showPercentile, setShowPercentile] = useState(false);
  const [subjects, setSubjects] = useState([]);

  // ✅ Extract all unique subjects from ALL tests (not just first one)
  useEffect(() => {
    if (data?.length > 0) {
      const allSubjects = new Map();

      data.forEach(({ basicAnalysis }) => {
        if (basicAnalysis?.subjectWise) {
          basicAnalysis.subjectWise.forEach((subject) => {
            if (subject?.subjectMeta?._id && !allSubjects.has(subject.subjectMeta._id)) {
              allSubjects.set(subject.subjectMeta._id, {
                _id: subject.subjectMeta._id,
                title: subject.subjectMeta.title,
                icon: subject.subjectMeta.icon,
                darkIcon: subject.subjectMeta.darkIcon,
                color: subject.subjectMeta.color,
              });
            }
          });
        }
      });

      setSubjects(Array.from(allSubjects.values()));
    }
  }, [data]);

  const onViewClick = (testId) => {
    router(`/${packId}/${testId}/analysis?source=pack_analysis_timeline`);
  };

  useEffect(() => {
    if (
      activeSubject === 'overall' &&
      (pack?.title?.includes('JEE Main 2025 Full Test Series') ||
        pack?.title?.includes('JEE Main 2025 Mini Test Series'))
    ) {
      setShowPercentile(true);
    } else {
      setShowPercentile(false);
    }
  }, [activeSubject, pack]);

  const getPercentileText = (percentileData, score) => {
    const percentile = findPercentile({ data: percentileData?.data, score: Number(score) });
    return percentile?.title ? `${percentile.title} ${percentile?.data}` : percentile?.data ? percentile?.data : '-';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center p-8 border border-[var(--q3-stroke-light)] rounded-lg">
        <NoData onResetAll={onResetFilters} />
      </div>
    );
  }

  return (
    <div className="">
      <TableHeader headerTitle="Test-wise Breakdown" showBottomBorder={false} />

      {/* SubjectTabs in its own div with horizontal scroll */}
      <div className="overflow-x-auto border-b border-r border-l border-[var(--q3-stroke-light)] bg-white mb-[-1px]">
        <div className="min-w-0 md:min-w-0">
          <SubjectTabs activeSubject={activeSubject} onSubjectChange={setActiveSubject} subjects={subjects} />
        </div>
      </div>

      {/* Main content container */}
      <div className="overflow-x-auto">
        <div className="min-w-[800px] md:min-w-0 border-l border-r border-b border-[var(--q3-stroke-light)] rounded-b-lg overflow-hidden">
          {/* Table Header */}
          <div className="bg-[var(--q3-surface-dim)] border-b border-[var(--q3-stroke-light)]">
            <div
              className={`grid ${
                showPercentile ? 'grid-cols-[30%_12%_12%_12%_12%_12%_10%]' : 'grid-cols-[30%_14%_14%_14%_14%_14%]'
              } divide-x divide-[var(--q3-stroke-light)] text-sm font-medium text-[var(--q3-neutral-default)]`}
            >
              <div className="p-4 flex items-center justify-start">Test</div>

              {showPercentile && (
                <div className="p-4 text-center text-nowrap">
                  <div>Percentile</div>
                </div>
              )}

              <div className="p-4 text-center text-[var(--q3-orange-normal)]">
                <div>Score</div>
              </div>
              <div className="p-4 text-center text-[var(--q3-green-normal)]">
                <div>Qs Attempted</div>
              </div>
              <div className="p-4 text-center text-[var(--q3-danger-normal)]">
                <div>Accuracy</div>
              </div>
              <div className="p-4 text-center text-[var(--q3-indigo-normal)]">
                <div>Time Taken</div>
              </div>
              <div className="p-4 text-center text-[var(--q3-neutral-light)]">
                <div>Complete Analysis</div>
              </div>
            </div>
          </div>

          {/* Table Body */}
          {data.map(({ basicAnalysis, testId }, index) => {
            let subjectData;

            if (activeSubject === 'overall') {
              subjectData = basicAnalysis?.subjectWise?.find((s) => s.subjectMeta?._id === 'overall');
            } else {
              subjectData = basicAnalysis?.subjectWise?.find((subject) => subject?.subjectMeta?._id === activeSubject);
            }

            if (!subjectData) return null;

            return (
              <div key={index} className="border-b border-[var(--q3-stroke-light)] last:border-b-0">
                <div
                  className={`grid ${
                    showPercentile ? 'grid-cols-[30%_12%_12%_12%_12%_12%_10%]' : 'grid-cols-[30%_14%_14%_14%_14%_14%]'
                  } divide-x divide-[var(--q3-stroke-light)] text-sm text-[var(--q3-neutral-default)]`}
                >
                  {/* Test Title + Date */}
                  <div className="ml-4 cursor-pointer" onClick={() => onViewClick(testId)}>
                    <div className="font-medium truncate max-w-full items-center justify-start mt-4">
                      {basicAnalysis?.testTitle}
                    </div>
                    <div className="text-xs text-[var(--q3-neutral-light)]">
                      {new Date(basicAnalysis?.submittedAt).toLocaleDateString('en-US', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </div>
                  </div>

                  {/* Percentile */}
                  {showPercentile && (
                    <div className="  text-center font-medium">
                      <div>
                        {basicAnalysis?.percentileData?.isAvailable
                          ? getPercentileText(basicAnalysis?.percentileData?.percentileData, subjectData?.marksScored)
                          : '-'}
                      </div>
                    </div>
                  )}

                  {/* Score */}
                  <div className=" text-center text-orange-600">
                    <StatCell
                      color="orange"
                      top={subjectData?.marksScored}
                      bottom={subjectData?.totalMarks}
                      themeText={true}
                      {...QSTATUS.AVERAGE}
                    />
                  </div>

                  {/* Attempted */}
                  <div className=" text-center text-green-600">
                    <StatCell
                      color="green"
                      top={subjectData?.questionAttempted}
                      bottom={subjectData?.totalQuestions}
                      {...QSTATUS.CORRECT}
                    />
                  </div>

                  {/* Accuracy */}
                  <div className=" text-center text-red-600">
                    <StatCell
                      color="red"
                      top={subjectData?.accuracy}
                      bottom={100}
                      showPercentage={true}
                      {...QSTATUS.INCORRECT}
                    />
                  </div>

                  {/* Time Taken */}
                  <div className="text-center text-blue-600">
                    <StatCell
                      color="blue"
                      top={secondsToMinutes(subjectData?.timeTaken || 0)}
                      bottom={subjectData?.totalTime || 0}
                      textAppend="min"
                      {...QSTATUS.NOT_ATTEMPTED}
                    />
                  </div>

                  {/* CTA: View Analysis */}
                  <div className=" text-center">
                    <button
                      onClick={() => onViewClick(testId)}
                      className="flex items-center gap-1 text-[var(--q3-accent-normal)] font-medium justify-center cursor-pointer ml-8 mt-6"
                    >
                      View <CaretRightIcon size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TimelineBreakdown;
